import classNames from "classnames";
import kebabCase from "lodash/kebabCase";
import trim from "lodash/trim";
import values from "lodash/values";
import PropTypes from "prop-types";
import React, { Component } from "react";

import ChatActions from "../../actions/ChatActions";
import CustomerHappinessType from "../../constants/ChatCustomerHappinessTypes";
import HappinessType from "../../constants/ChatHappinessTypes";
import RatingSystemType from "../../constants/ChatRatingSystemTypes";
import iconSprite from "../../../../images/img/chat/icons.svg";
import SvgIcon from "../common/SvgIcon";
import { createSanitizedMarkup, isNodeInRoot } from "../../utils/Utils";

class ToolbarDefault extends Component {
    constructor() {
        super();

        this.state = {
            disableAcceptTooltipIsShown: false,
        };

        this.getCustomerHappinessSvgId = this.getCustomerHappinessSvgId.bind(this);
        this.getStarCount = this.getStarCount.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onClickOutsideOfDisableAcceptTooltip = this.onClickOutsideOfDisableAcceptTooltip.bind(this);
        this.onToggleRateToolbar = this.onToggleRateToolbar.bind(this);
    }

    componentDidMount() {
        this.props.setOffsetBottomOfMessageList();
    }

    componentWillReceiveProps(nextProps) {
        if (!nextProps.disableAcceptButton && this.state.disableAcceptTooltipIsShown) {
            this.setState({ disableAcceptTooltipIsShown: false });
        }
    }

    componentWillUnmount() {
        /*
         * setTimeout is needed to make sure ToolbarDefault is not in DOM anymore
         * when computing new bottom offset
         */
        setTimeout(this.props.setOffsetBottomOfMessageList, 80);
    }

    onToggleRateToolbar() {
        ChatActions.responseToggleRateToolbar();
    }

    onClickOutsideOfDisableAcceptTooltip(event) {
        const { disableAcceptTooltipTrigger, disableAcceptTooltip } = this.refs;
        const clickedTooltip = isNodeInRoot(event.target, disableAcceptTooltip);
        const clickedTooltipTrigger = isNodeInRoot(event.target, disableAcceptTooltipTrigger);

        if (clickedTooltip || clickedTooltipTrigger) {
            event.preventDefault();
        } else {
            this.setState({ disableAcceptTooltipIsShown: false });
            document.removeEventListener("click", this.onClickOutsideOfDisableAcceptTooltip);
        }
    }

    onClick() {
        this.setState({ disableAcceptTooltipIsShown: this.props.disableAcceptButton });
        document.addEventListener("click", this.onClickOutsideOfDisableAcceptTooltip);
    }

    getCustomerHappinessSvgId() {
        const { customerHappiness, happiness } = this.props.response;
        const customerHappinessToSvgIdMap = {
            [CustomerHappinessType.POSITIVE_2]: "smiley-green",
            [CustomerHappinessType.NEGATIVE_2]: "smiley-red",
            [CustomerHappinessType.POSITIVE_3]: "smiley-green",
            [CustomerHappinessType.NEUTRAL_3]: "smiley-yellow",
            [CustomerHappinessType.NEGATIVE_3]: "smiley-red",
            [CustomerHappinessType.POSITIVE_4]: "smiley-green",
            [CustomerHappinessType.NEUTRAL_POSITIVE_4]: "smiley-yellow",
            [CustomerHappinessType.NEUTRAL_NEGATIVE_4]: "smiley-orange",
            [CustomerHappinessType.NEGATIVE_4]: "smiley-red",
            [CustomerHappinessType.POSITIVE_5]: "smiley-green",
            [CustomerHappinessType.NEUTRAL_POSITIVE_5]: "smiley-green-less-excited",
            [CustomerHappinessType.NEUTRAL_5]: "smiley-yellow-straight-face",
            [CustomerHappinessType.NEUTRAL_NEGATIVE_5]: "smiley-orange",
            [CustomerHappinessType.NEGATIVE_5]: "smiley-red",
        };
        const happinessToSvgIdMap = {
            [HappinessType.negative]: "smiley-red",
            [HappinessType.neutral]: "smiley-yellow",
            [HappinessType.positive]: "smiley-green",
        };

        return customerHappinessToSvgIdMap[customerHappiness] || happinessToSvgIdMap[happiness];
    }

    getStarCount() {
        const { customerHappiness, happiness } = this.props.response;
        const customerHappinessToStarCountMap = {
            [CustomerHappinessType.STARS_1]: 1,
            [CustomerHappinessType.STARS_2]: 2,
            [CustomerHappinessType.STARS_3]: 3,
            [CustomerHappinessType.STARS_4]: 4,
            [CustomerHappinessType.STARS_5]: 5,
        };
        const happinessToStarCountMap = {
            [HappinessType.negative]: 1,
            [HappinessType.neutral]: 3,
            [HappinessType.positive]: 5,
        };

        return customerHappinessToStarCountMap[customerHappiness] || happinessToStarCountMap[happiness];
    }

    render() {
        const {
            disableAcceptButton,
            hasAutoResponse,
            onAcceptAnswer,
            question,
            ratingSystem,
            response,
            showAcceptAnswerControl,
        } = this.props;
        const { disableAcceptTooltipIsShown } = this.state;
        const questionIsClosed = Boolean(question.dateClosed);
        const responseIsRated = response.happiness !== "not-rated";
        const ratedButtonClasses = classNames({
            "rate-btn": true,
            "star-btn": ratingSystem === RatingSystemType.fiveStars,
            "rate-btn-single": true,
            [`happiness-${response.happiness}`]: true,
            [`customer-happiness-${kebabCase(response.customerHappiness)}`]: true,
        });

        let acceptAnswerControl = null;
        if (questionIsClosed && response.isAccepted) {
            acceptAnswerControl = (
                <div className="toolbar-btn">
                    <div className="btn-content-wrapper">
                        <p>
                            <strong>{i18n.t("rtm.text.answer_accepted")}</strong>
                        </p>
                    </div>
                </div>
            );
        } else if (questionIsClosed && responseIsRated) {
            acceptAnswerControl = (
                <div className="toolbar-btn">
                    <div className="btn-content-wrapper">
                        <p className="feedback-message">
                            <strong>{i18n.t("rtm.text.thanks_for_your_feedback")}</strong>
                        </p>
                    </div>
                </div>
            );
        } else if (!questionIsClosed) {
            acceptAnswerControl = (
                <div className="tooltip-wrapper inverted-skin">
                    {disableAcceptTooltipIsShown && (
                        <span className="tooltip-content" ref="disableAcceptTooltip">
                            {i18n.t("rtm.text.you_can_accept_the_answer_after")}
                        </span>
                    )}

                    {/*
                     * This element exists because if the underneath element is
                     * disabled (e.g. a button), events (e.g. mouseleave) from
                     * that element won't bubble up; this element basically
                     * covers the disabled element and the events will bubble
                     * from this element up instead from the one(s) underneath.
                     */}
                    {disableAcceptButton && (
                        <div
                            className="tooltip-trigger"
                            onClick={this.onClick}
                            onTouchStart={this.onClick}
                            ref="disableAcceptTooltipTrigger"
                        />
                    )}

                    {/*
                     * The button has to be the last element of parent or else
                     * the button will have a right border since it won't be
                     * the :last-child anymore
                     */}
                    <button
                        type="button"
                        className="toolbar-btn"
                        disabled={disableAcceptButton}
                        onClick={onAcceptAnswer}
                        tabIndex={0}
                        name={i18n.t("rtm.text.accept_answer")}
                    >
                        <span className="btn-content-wrapper">
                            <SvgIcon src={iconSprite} id="icon-checkmark" />
                            <strong>{i18n.t("rtm.text.accept_answer")}</strong>
                        </span>
                    </button>
                </div>
            );
        }

        return (
            <div className="toolbar-container toolbar-default-container">
                {responseIsRated ? (
                    <button
                        type="button"
                        className={ratedButtonClasses}
                        onClick={this.onToggleRateToolbar}
                        tabIndex={0}
                    >
                        {ratingSystem === RatingSystemType.fiveStars ? (
                            <span className="star-rating-container">
                                <SvgIcon src={iconSprite} id="icon-star" />
                                <strong className="star-count">{this.getStarCount()}</strong>
                            </span>
                        ) : (
                            <SvgIcon src={iconSprite} id={this.getCustomerHappinessSvgId()} />
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="toolbar-btn"
                        name={trim(i18n.t("rtm.text.rate"))}
                        onClick={this.onToggleRateToolbar}
                        tabIndex={0}
                    >
                        <span className="btn-content-wrapper">
                            {ratingSystem === RatingSystemType.fiveStars ? (
                                <span className="rate-icon star-icon">
                                    <span className="star">
                                        <SvgIcon id="icon-star" src={iconSprite} />
                                    </span>
                                </span>
                            ) : (
                                <span className="rate-icon">
                                    <span className="dot dot-green" />
                                    <span className="dot dot-yellow" />
                                    <span className="dot dot-red" />
                                </span>
                            )}

                            {hasAutoResponse ? (
                                <strong>Rate</strong>
                            ) : (
                                <strong
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={createSanitizedMarkup(
                                        i18n.t("rtm.text.rate", {
                                            recognitionRecipientName: response.recognitionRecipient.name,
                                        })
                                    )}
                                />
                            )}
                        </span>
                    </button>
                )}

                {showAcceptAnswerControl && acceptAnswerControl}
            </div>
        );
    }
}

ToolbarDefault.propTypes = {
    disableAcceptButton: PropTypes.bool.isRequired,
    hasAutoResponse: PropTypes.bool.isRequired,
    onAcceptAnswer: PropTypes.func.isRequired,
    question: PropTypes.shape({}).isRequired,
    ratingSystem: PropTypes.oneOf(values(RatingSystemType)).isRequired,
    response: PropTypes.shape({
        happiness: PropTypes.string,
        customerHappiness: PropTypes.string,
    }).isRequired,
    setOffsetBottomOfMessageList: PropTypes.func.isRequired,
    showAcceptAnswerControl: PropTypes.bool.isRequired,
};

export default ToolbarDefault;
